import React, { useMemo, useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Chip,
  IconButton,
  Divider,
  Collapse,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  ChatBubbleOutline as ChatIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import type { Conversation } from '../types';

interface SessionListProps {
  conversations: Conversation[];
  activeConversationId: string | null;
  onSelectConversation: (id: string) => void;
  onNewConversation: () => void;
  isLoading?: boolean;
}

interface GroupedConversations {
  label: string;
  conversations: Conversation[];
}

export const SessionList: React.FC<SessionListProps> = ({
  conversations,
  activeConversationId,
  onSelectConversation,
  onNewConversation,
  isLoading = false,
}) => {
  // Track which groups are expanded (all collapsed by default)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (label: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(label)) {
        newSet.delete(label);
      } else {
        newSet.add(label);
      }
      return newSet;
    });
  };

  const getConversationPreview = (conversation: Conversation): string => {
    if (conversation.conversations && conversation.conversations.length > 0) {
      const lastMessage = conversation.conversations[0];
      return lastMessage.content.user.substring(0, 50) + (lastMessage.content.user.length > 50 ? '...' : '');
    }
    return 'New conversation';
  };

  // Melbourne timezone for consistent date handling
  const TIMEZONE = 'Australia/Melbourne';

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-AU', {
      timeZone: TIMEZONE,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper function to get date string in Melbourne timezone (YYYY-MM-DD format)
  const getMelbourneDateString = (date: Date): string => {
    return date.toLocaleDateString('en-CA', { timeZone: TIMEZONE }); // en-CA gives YYYY-MM-DD format
  };

  // Filter out empty conversations and group by time period
  const groupedConversations = useMemo(() => {
    // Filter out empty conversations
    const nonEmptyConversations = conversations.filter(
      (conv) => conv.messageCount && conv.messageCount > 0
    );

    if (nonEmptyConversations.length === 0) {
      return [];
    }

    // Get current date in Melbourne timezone
    const now = new Date();
    const todayStr = getMelbourneDateString(now);

    // Calculate yesterday's date string
    const yesterdayDate = new Date(now);
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    const yesterdayStr = getMelbourneDateString(yesterdayDate);

    // Calculate this week's start (Sunday) in Melbourne
    const thisWeekStartDate = new Date(now);
    // Get actual day of week (0=Sun, 1=Mon, etc.) in Melbourne timezone
    const melbourneDay = new Date(now.toLocaleString('en-US', { timeZone: TIMEZONE })).getDay();
    thisWeekStartDate.setDate(thisWeekStartDate.getDate() - melbourneDay);
    const thisWeekStartStr = getMelbourneDateString(thisWeekStartDate);

    // Calculate this month's start
    const melbourneNow = new Date(now.toLocaleString('en-US', { timeZone: TIMEZONE }));
    const thisMonthStartStr = `${melbourneNow.getFullYear()}-${String(melbourneNow.getMonth() + 1).padStart(2, '0')}-01`;

    // Calculate this year's start
    const thisYearStartStr = `${melbourneNow.getFullYear()}-01-01`;

    const groups: { [key: string]: Conversation[] } = {
      'Today': [],
      'Yesterday': [],
      'This Week': [],
      'This Month': [],
      'This Year': [],
      'Older': [],
    };

    // Debug logging
    console.log('Date boundaries (Melbourne):', {
      todayStr,
      yesterdayStr,
      thisWeekStartStr,
      thisMonthStartStr,
      thisYearStartStr,
    });

    nonEmptyConversations.forEach((conv) => {
      // Get conversation date in Melbourne timezone
      const convDate = new Date(conv.lastUpdateTime);
      const convDateStr = getMelbourneDateString(convDate);

      // Simple grouping: Today, Yesterday, then everything else by relative period
      if (convDateStr === todayStr) {
        groups['Today'].push(conv);
      } else if (convDateStr === yesterdayStr) {
        groups['Yesterday'].push(conv);
      } else if (convDateStr >= thisWeekStartStr) {
        // Earlier this week (not today or yesterday)
        groups['This Week'].push(conv);
      } else if (convDateStr >= thisMonthStartStr) {
        // Earlier this month (not this week)
        groups['This Month'].push(conv);
      } else if (convDateStr >= thisYearStartStr) {
        // Earlier this year (not this month)
        groups['This Year'].push(conv);
      } else {
        groups['Older'].push(conv);
      }
    });

    // Debug: log group counts
    console.log('Group counts:', Object.fromEntries(
      Object.entries(groups).map(([k, v]) => [k, v.length])
    ));

    // Build result array with only non-empty groups
    const result: GroupedConversations[] = [];
    const groupOrder = ['Today', 'Yesterday', 'This Week', 'This Month', 'This Year', 'Older'];

    groupOrder.forEach((label) => {
      if (groups[label].length > 0) {
        result.push({
          label,
          conversations: groups[label],
        });
      }
    });

    return result;
  }, [conversations]);

  const totalNonEmpty = groupedConversations.reduce(
    (sum, group) => sum + group.conversations.length,
    0
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Conversations</Typography>
        <IconButton color="primary" onClick={onNewConversation} size="small">
          <AddIcon />
        </IconButton>
      </Box>

      <Divider />

      <List sx={{ flexGrow: 1, overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress size={32} sx={{ mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Loading conversations...
            </Typography>
          </Box>
        ) : totalNonEmpty === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <ChatIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No conversations yet
            </Typography>
          </Box>
        ) : (
          groupedConversations.map((group) => (
            <React.Fragment key={group.label}>
              {/* Collapsible Group Header */}
              <ListItemButton
                onClick={() => toggleGroup(group.label)}
                sx={{
                  py: 0.5,
                  px: 2,
                  bgcolor: 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' },
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    flexGrow: 1,
                  }}
                >
                  {group.label} ({group.conversations.length})
                </Typography>
                {expandedGroups.has(group.label) ? (
                  <ExpandLessIcon fontSize="small" color="action" />
                ) : (
                  <ExpandMoreIcon fontSize="small" color="action" />
                )}
              </ListItemButton>

              {/* Collapsible Content */}
              <Collapse in={expandedGroups.has(group.label)} timeout="auto" unmountOnExit>
                {group.conversations.map((conversation) => (
                  <ListItem key={conversation.id} disablePadding>
                    <ListItemButton
                      selected={conversation.id === activeConversationId}
                      onClick={() => onSelectConversation(conversation.id)}
                      sx={{ py: 1, px: 2, pl: 3 }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" noWrap sx={{ flexGrow: 1, mr: 1 }}>
                              {getConversationPreview(conversation)}
                            </Typography>
                            <Chip
                              label={conversation.messageCount || 0}
                              size="small"
                              sx={{ minWidth: 35 }}
                            />
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(conversation.lastUpdateTime)}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </Collapse>
            </React.Fragment>
          ))
        )}
      </List>
    </Box>
  );
};
